import { type FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, ShieldAlert, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { IdentityApiError, useChangePassword, useSession } from "@/api/hooks";

export function ChangePasswordPage() {
    const session = useSession();
    const change = useChangePassword();
    const navigate = useNavigate();
    const [oldPassword, setOldPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [done, setDone] = useState(false);

    if (session.data && !session.data.isAuthenticated) {
        navigate("/login?returnUrl=/change-password", { replace: true });
    }

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);
        if (newPassword !== confirm) {
            setError("New password and confirmation do not match.");
            return;
        }
        if (newPassword.length < 12) {
            setError("New password must be at least 12 characters.");
            return;
        }
        try {
            await change.mutateAsync({ oldPassword, newPassword });
            setDone(true);
            setOldPassword("");
            setNewPassword("");
            setConfirm("");
        } catch (err) {
            if (err instanceof IdentityApiError) {
                setError(err.detail ? `${err.message} — ${err.detail}` : err.message);
            } else {
                setError("Password change failed.");
            }
        }
    };

    return (
        <Card className="w-full">
            <CardHeader>
                <CardTitle>Change password</CardTitle>
                <CardDescription>
                    Updates your Athavita password and re-syncs your mail server credentials so
                    Outlook / Thunderbird / iOS Mail keep working.
                </CardDescription>
            </CardHeader>
            <CardContent>
                {error ? (
                    <Alert variant="destructive" className="mb-4">
                        <ShieldAlert className="h-4 w-4" />
                        <AlertTitle>Couldn't change your password</AlertTitle>
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                ) : null}
                {done ? (
                    <Alert className="mb-4">
                        <ShieldCheck className="h-4 w-4" />
                        <AlertTitle>Password changed</AlertTitle>
                        <AlertDescription>
                            Your next web sign-in and your next IMAP / SMTP connection will use
                            the new password.
                        </AlertDescription>
                    </Alert>
                ) : null}
                <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                    <div className="space-y-1.5">
                        <Label htmlFor="old-password">Current password</Label>
                        <Input
                            id="old-password"
                            type="password"
                            autoComplete="current-password"
                            required
                            value={oldPassword}
                            onChange={(e) => setOldPassword(e.target.value)}
                            autoFocus
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="new-password">New password</Label>
                        <Input
                            id="new-password"
                            type="password"
                            autoComplete="new-password"
                            required
                            minLength={12}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                        />
                        <p className="text-xs text-muted-foreground">At least 12 characters.</p>
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="confirm">Confirm new password</Label>
                        <Input
                            id="confirm"
                            type="password"
                            autoComplete="new-password"
                            required
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                        />
                    </div>
                    <Button type="submit" className="w-full" disabled={change.isPending}>
                        <KeyRound className="h-4 w-4" />
                        {change.isPending ? "Changing…" : "Change password"}
                    </Button>
                </form>
            </CardContent>
            <CardFooter className="text-sm text-muted-foreground">
                Signed in as {session.data?.email ?? "—"}
            </CardFooter>
        </Card>
    );
}
